import type { ErrorEvent } from "@sentry/react";

const DIAGNOSTIC_FINGERPRINT = "browser-connectivity-diagnostics";
const CONTEXT_KEYS = [
  "schema",
  "category",
  "incidentId",
  "windowMinutes",
  "operationName",
  "refreshSource",
  "refreshResult",
  "responseStatus",
  "responseChallenge",
  "decision",
  "recoveryOutcome",
  "triggerKind",
  "httpResult",
  "endpoint",
  "countersJson",
  "sequenceJson",
] as const;

export type BrowserFamily =
  | "edge"
  | "chrome"
  | "firefox"
  | "safari"
  | "opera"
  | "samsung"
  | "other";
export type OsFamily =
  | "windows"
  | "macos"
  | "ios"
  | "android"
  | "chromeos"
  | "linux"
  | "other";

export interface BrowserCohort {
  browserFamily: BrowserFamily;
  browserMajor: number;
  osFamily: OsFamily;
}

function boundedMajor(match: RegExpMatchArray | null): number {
  if (!match?.[1] || !/^\d+$/.test(match[1])) return 0;
  const value = Number(match[1]);
  return Number.isInteger(value) && value >= 1 && value <= 999 ? value : 0;
}

export function classifyBrowserCohort(userAgent: string): BrowserCohort {
  let browserFamily: BrowserFamily = "other";
  let version: RegExpMatchArray | null = null;

  if ((version = userAgent.match(/\bEdg(?:A|iOS)?\/(\d+)/i))) {
    browserFamily = "edge";
  } else if ((version = userAgent.match(/\b(?:OPR|OPiOS)\/(\d+)/i))) {
    browserFamily = "opera";
  } else if ((version = userAgent.match(/\bSamsungBrowser\/(\d+)/i))) {
    browserFamily = "samsung";
  } else if ((version = userAgent.match(/\bCriOS\/(\d+)/i))) {
    browserFamily = "chrome";
  } else if ((version = userAgent.match(/\bFxiOS\/(\d+)/i))) {
    browserFamily = "firefox";
  } else if ((version = userAgent.match(/\b(?:Chrome|Chromium)\/(\d+)/i))) {
    browserFamily = "chrome";
  } else if ((version = userAgent.match(/\bFirefox\/(\d+)/i))) {
    browserFamily = "firefox";
  } else if (/\bSafari\//i.test(userAgent)) {
    version = userAgent.match(/\bVersion\/(\d+)/i);
    browserFamily = "safari";
  }

  const osFamily: OsFamily = /\bCrOS\b/i.test(userAgent)
    ? "chromeos"
    : /\b(?:iPhone|iPad|iPod)\b/i.test(userAgent) ||
        (/\bMacintosh\b/i.test(userAgent) && /\bMobile\//i.test(userAgent))
      ? "ios"
      : /\bAndroid\b/i.test(userAgent)
        ? "android"
        : /\bWindows\b/i.test(userAgent)
          ? "windows"
          : /\b(?:Macintosh|Mac OS X)\b/i.test(userAgent)
            ? "macos"
            : /\bLinux\b/i.test(userAgent)
              ? "linux"
              : "other";

  return {
    browserFamily,
    browserMajor: boundedMajor(version),
    osFamily,
  };
}

function nativeBrowserCohort(): BrowserCohort {
  try {
    return classifyBrowserCohort(globalThis.navigator?.userAgent ?? "");
  } catch {
    return classifyBrowserCohort("");
  }
}

export function sanitizeBrowserConnectivityEvent(event: ErrorEvent): ErrorEvent {
  if (event.fingerprint?.[0] !== DIAGNOSTIC_FINGERPRINT) return event;

  const source = event.contexts?.browserConnectivity;
  const browserConnectivity: Record<string, string | number | boolean> = {};
  for (const key of CONTEXT_KEYS) {
    const value = source?.[key];
    if (
      typeof value === "string" ||
      typeof value === "number" ||
      typeof value === "boolean"
    ) {
      browserConnectivity[key] = value;
    }
  }
  const cohort = nativeBrowserCohort();
  Object.assign(browserConnectivity, cohort);

  const sanitized: ErrorEvent = {
    type: undefined,
    contexts: { browserConnectivity },
    tags: {
      "diagnostic.browser": cohort.browserFamily,
      "diagnostic.browser_major": String(cohort.browserMajor),
      "diagnostic.os": cohort.osFamily,
    },
  };
  if (event.event_id !== undefined) sanitized.event_id = event.event_id;
  if (event.timestamp !== undefined) sanitized.timestamp = event.timestamp;
  if (event.message !== undefined) sanitized.message = event.message;
  if (event.level !== undefined) sanitized.level = event.level;
  if (event.fingerprint !== undefined) sanitized.fingerprint = [...event.fingerprint];
  if (event.release !== undefined) sanitized.release = event.release;
  if (event.environment !== undefined) sanitized.environment = event.environment;
  if (event.dist !== undefined) sanitized.dist = event.dist;
  if (event.platform !== undefined) sanitized.platform = event.platform;
  if (event.sdk !== undefined) sanitized.sdk = { ...event.sdk };
  return sanitized;
}
